import { useState } from 'react'

export default function ContactForm({ title = 'KONTAKT', subtitle = 'Få et uforpligtende tilbud til din næste fest' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postalCode: '',
    eventDate: '',
    eventType: 'Firmafest',
    guestsCount: '50-100',
    description: '',
    website_url: '' // Honeypot field
  })

  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [apiError, setApiError] = useState('')

  const validate = () => {
    const errs = {}
    if (!formData.name.trim()) errs.name = 'Venligst indtast dit navn'
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din email'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errs.email = 'Ugyldig emailadresse'
    }
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst indtast dit telefonnummer'
    }
    if (!formData.postalCode.trim()) {
      errs.postalCode = 'Venligst indtast postnummer'
    }
    if (!formData.description.trim()) {
      errs.description = 'Venligst skriv en kort beskrivelse af dit arrangement'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setApiError('')

    if (!validate()) return

    // Antispam: if honeypot is filled, silent reject
    if (formData.website_url) {
      setSubmitted(true)
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        postalCode: formData.postalCode,
        eventDate: formData.eventDate,
        eventType: formData.eventType,
        guestsCount: formData.guestsCount,
        description: formData.description,
        website_url: formData.website_url // Required by Auxo webform integration
      }

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (res.ok) {
        setSubmitted(true)
      } else {
        const data = await res.json().catch(() => ({}))
        setApiError(data.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte.')
      }
    } catch (err) {
      setApiError('Der kunne ikke oprettes forbindelse. Prøv venligst igen senere.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-neutral-900/60 border border-neutral-800 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xl">
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-neutral-400 text-sm sm:text-base mt-2">
            {subtitle}
          </p>
        )}
      </div>

      {submitted ? (
        <div className="p-8 text-center bg-gold-400/10 border border-gold-400/30 rounded-xl space-y-4">
          <div className="w-16 h-16 bg-gold-400 text-black rounded-full flex items-center justify-center mx-auto text-2xl">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h3 className="text-xl font-bold text-white">Tak for din henvendelse!</h3>
          <p className="text-neutral-300 text-sm max-w-md mx-auto">
            Jeg har modtaget din besked og vender tilbage hurtigst muligt med et skræddersyet tilbud til din fest.
          </p>
          <button
            type="button"
            onClick={() => {
              setSubmitted(false)
              setFormData({
                name: '',
                email: '',
                phone: '',
                postalCode: '',
                eventDate: '',
                eventType: 'Firmafest',
                guestsCount: '50-100',
                description: '',
                website_url: ''
              })
            }}
            className="inline-block px-5 py-2.5 rounded-lg bg-neutral-800 text-neutral-200 text-sm font-medium hover:bg-neutral-700 transition-colors cursor-pointer mt-2"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {apiError && (
            <div className="p-4 bg-red-950/60 border border-red-800 rounded-lg text-red-200 text-sm flex items-center gap-2.5">
              <i className="fa-solid fa-circle-exclamation text-red-400 shrink-0" aria-hidden="true" />
              <span>{apiError}</span>
            </div>
          )}

          {/* Antispam honeypot - invisible to human users */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
                Navn <span className="text-gold-400 font-normal">(påkrævet)</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className={`w-full px-4 py-3 bg-neutral-950 border rounded-xl text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors ${
                  errors.name ? 'border-red-500' : 'border-neutral-800'
                }`}
              />
              {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
                Email <span className="text-gold-400 font-normal">(påkrævet)</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className={`w-full px-4 py-3 bg-neutral-950 border rounded-xl text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors ${
                  errors.email ? 'border-red-500' : 'border-neutral-800'
                }`}
              />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {/* Tlf. Nr. */}
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
                Tlf. Nr. <span className="text-gold-400 font-normal">(påkrævet)</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+45 12 34 56 78"
                className={`w-full px-4 py-3 bg-neutral-950 border rounded-xl text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors ${
                  errors.phone ? 'border-red-500' : 'border-neutral-800'
                }`}
              />
              {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
            </div>

            {/* Postnr. */}
            <div>
              <label htmlFor="postalCode" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
                Postnr. <span className="text-gold-400 font-normal">(påkrævet)</span>
              </label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                placeholder="F.eks. 5000"
                className={`w-full px-4 py-3 bg-neutral-950 border rounded-xl text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors ${
                  errors.postalCode ? 'border-red-500' : 'border-neutral-800'
                }`}
              />
              {errors.postalCode && <p className="text-red-400 text-xs mt-1">{errors.postalCode}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:gap-5">
            {/* Dato for event */}
            <div>
              <label htmlFor="eventDate" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
                Dato for event
              </label>
              <input
                type="date"
                id="eventDate"
                name="eventDate"
                value={formData.eventDate}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-neutral-950 border border-neutral-800 rounded-xl text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors"
              />
            </div>

            {/* Fest type */}
            <div>
              <label htmlFor="eventType" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
                Fest type
              </label>
              <select
                id="eventType"
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-neutral-950 border border-neutral-800 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors"
              >
                <option value="Firmafest">Firmafest</option>
                <option value="Privatfest">Privatfest</option>
                <option value="Bryllup">Bryllup</option>
                <option value="Andet">Andet</option>
              </select>
            </div>

            {/* Antal deltagere */}
            <div>
              <label htmlFor="guestsCount" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
                Antal deltagere <span className="text-gold-400 font-normal">(påkrævet)</span>
              </label>
              <select
                id="guestsCount"
                name="guestsCount"
                value={formData.guestsCount}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-neutral-950 border border-neutral-800 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors"
              >
                <option value="0-30">0-30</option>
                <option value="31-50">31-50</option>
                <option value="50-100">50-100</option>
                <option value="100 - 200">100 - 200</option>
                <option value="200+">200+</option>
              </select>
            </div>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="description" className="block text-xs font-semibold text-neutral-300 mb-1.5 uppercase tracking-wider">
              Beskrivelse / Kommentarer <span className="text-gold-400 font-normal">(påkrævet)</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              placeholder="Fortæl lidt om arrangementet, spilletider, sted, specifikke musikønsker osv."
              className={`w-full px-4 py-3 bg-neutral-950 border rounded-xl text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-gold-400/80 transition-colors ${
                errors.description ? 'border-red-500' : 'border-neutral-800'
              }`}
            />
            {errors.description && <p className="text-red-400 text-xs mt-1">{errors.description}</p>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-8 py-3.5 bg-gold-400 text-neutral-950 font-bold text-sm uppercase tracking-wider rounded-xl hover:bg-gold-300 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:ring-offset-2 focus:ring-offset-neutral-900 transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                <span>Sender forespørgsel...</span>
              </>
            ) : (
              <>
                <span>Send forespørgsel</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
